import axios from 'axios';
import { LocalStorage } from './helpers';
import { initializeCookieAuth } from './helpers/CookieAuth';
import { isFailureForCurrentSession } from './helpers/SessionToken';

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 600000, // timeout 10 minutes due to on production we have alot of datas
});

// ROI-138: Shortlink (/sl/) and Baitlink (/bl/) are public, anonymous, cross-origin pages
// resolving a link tapped from an SMS/email — same reasoning as /profile below, an
// Authorization header here is a "non-simple" request header that forces a CORS preflight
// the anonymous LinkTracker endpoints they call can't satisfy (EnableEndpointRouting=false
// on the API means preflight only ever sees the credentialed default CORS policy, not the
// per-action [EnableCors] on those routes -- see roi-138-cors-fix-followup-diagnosis.md).
const NO_AUTH_HEADER_PATH_PREFIXES = ["/profile", "/sl/", "/bl/"];

instance.interceptors.request.use(config => {
  if (!NO_AUTH_HEADER_PATH_PREFIXES.some(prefix => window.location.pathname.startsWith(prefix))) {
    // attach headers Authorization if URL not startsWith one of the above
    config.headers.Authorization = {
      toString() {
        // Check cookie first for SSO support, then localStorage
        const cookieToken = initializeCookieAuth();
        const localToken = LocalStorage.loadToken();
        const data = cookieToken || localToken;

        // ROI-435: remember the token this request actually carried. The header is read lazily, here,
        // so reading config.headers.Authorization later would give the token stored THEN, not this one.
        config.__roiSentToken = data || '';

        if (data) {
          return `Bearer ${data}`;
        }
      },
    }
  }
  return config;
});

instance.interceptors.response.use(undefined, err => {
  if (!err.response || err.response.config?.__isRetryRequest) {
    return Promise.reject(err);
  }

  const { status, config } = err.response;
  const url = config?.url || '';

  // Clear auth for invalid tokens:
  // 1. 400 on /Users/ endpoint (invalid token causes "User not found!")
  // 2. 401 = Unauthorized (invalid/expired token)
  // Some /Users/ endpoints also live under /Users/ but legitimately return 400
  // for non-auth, business reasons, which must NOT trigger a logout:
  // - ForgotPassword / ResetPassword: e.g. SendGrid email delivery failure.
  // - Create / Update: e.g. duplicate email or validation errors when an admin
  //   manages other users; the current session is still valid.
  // - ChangePassword: e.g. a wrong current password; the session is still valid
  //   and the form shows an inline error instead of logging the user out.
  // - SignOutOtherDevices (ROI-435): a failure there leaves this session as it was.
  const nonAuth400Endpoints = [
    '/Users/ForgotPassword',
    '/Users/ResetPassword',
    '/Users/Create',
    '/Users/Update',
    '/Users/ChangePassword',
    '/Users/SignOutOtherDevices',
  ];
  const isNonAuth400Endpoint = nonAuth400Endpoints.some(endpoint => url.includes(endpoint));
  const shouldClearAuth =
    status === 401 ||
    (status === 400 && url.includes('/Users/') && !isNonAuth400Endpoint);

  // ROI-435: a password change or "Sign out other devices" stores a fresh token while older requests
  // (pollers, other tabs) are still in flight with the old one. Their late 401 is about the old token:
  // clearing on it would wipe the new token and sign every tab out. Clear only when the failed request
  // carried the token stored now (or there is none).
  if (shouldClearAuth && isFailureForCurrentSession(config?.__roiSentToken)) {
    LocalStorage.clearToken();
    LocalStorage.clearStateGeneral('user');
    const { clearCookieAuth } = require('./helpers/CookieAuth');
    clearCookieAuth();
    window.location.href = '/login';
  }

  return Promise.reject(err);
});

export default instance;
